<template>
  <div class="pw-page" :class="{ 'pw-page--loading': loadingList && !photos.length }">
    <!-- 背景装饰 -->
    <div class="pw-bg" aria-hidden="true">
      <span v-for="n in 18" :key="n" class="pw-bg__heart" :style="floatStyle(n)">♥</span>
    </div>

    <!-- Hero -->
    <header class="pw-hero">
      <h1>Our Little Corner</h1>
      <p>收藏属于我们的每一个瞬间，定格生活中安静的美好。</p>
    </header>

    <!-- 筛选条 -->
    <section class="pw-filter">
      <div class="pw-filter__row">
        <div class="pw-filter__input pw-filter__input--keyword">
          <span class="material-symbols-outlined" style="font-size:18px">search</span>
          <input v-model="filters.keyword" placeholder="搜索故事内容…" @input="onFilterChange">
        </div>
        <div class="pw-filter__input">
          <span class="material-symbols-outlined" style="font-size:18px">location_on</span>
          <input v-model="filters.location" placeholder="按地点搜索…" @input="onFilterChange">
        </div>
        <el-date-picker
          v-model="filters.dateRange"
          type="daterange"
          format="YYYY年M月D日"
          value-format="YYYY-MM-DD"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          class="pw-filter__date"
          @change="onFilterChange"
        />
        <button v-if="hasActiveFilter" class="pw-filter__clear" @click="clearFilters">
          <span class="material-symbols-outlined" style="font-size:16px">close</span> 清空
        </button>
      </div>
    </section>

    <!-- 空态 -->
    <div v-if="!loadingList && !photos.length" class="pw-empty">
      <span class="material-symbols-outlined" style="font-size:56px;color:var(--pw-outline-variant)">photo_library</span>
      <p>{{ isVisitor ? '暂无相册' : '这面墙还空着，先创建第一本相册吧' }}</p>
      <button v-if="!isVisitor" class="pw-btn-primary" @click="openCreate">
        <span class="material-symbols-outlined" style="font-size:18px">add</span> 创建相册
      </button>
    </div>

    <!-- 照片墙（按年月分组） -->
    <div v-else ref="wallRef" class="pw-groups" v-loading="loadingList && !photos.length">
      <section v-for="group in groupedPhotos" :key="group.year" class="pw-year">
        <div class="pw-year__bar">
          <h2 class="pw-year__title">{{ group.title }}</h2>
          <span class="pw-year__line"></span>
          <span class="pw-year__count">{{ group.count }} 本</span>
        </div>
        <div v-for="m in group.months" :key="m.key" class="pw-month">
          <h3 class="pw-month__title">{{ m.label }} <small>{{ m.photos.length }} 本</small></h3>
          <section class="pw-wall">
            <article
              v-for="(photo, index) in m.photos"
              :key="photo.id"
              class="pw-photo"
              :style="photoStyle(photo, index)"
              @mousemove="onTilt"
              @mouseleave="onTiltLeave"
              @click="openDetail(photo)"
            >
              <span class="pw-photo__tape" :style="tapeStyle(photo.id)"></span>
              <div class="pw-photo__inner">
                <div class="pw-photo__img-wrap">
                  <img v-if="getAlbumCover(photo)" :src="getAlbumCover(photo)" :alt="photo.title || photo.description || '相册封面'" loading="lazy" draggable="false">
                  <div v-else class="pw-photo__media-card pw-photo__media-card--fallback">
                    <span class="material-symbols-outlined" style="font-size:40px">perm_media</span>
                    <span>{{ mediaTypeLabel(getMediaType(photo)) }}</span>
                  </div>
                  <span v-if="(photo.mediaCount || albumMediaList(photo).length) > 1" class="pw-photo__count-badge">
                    {{ photo.mediaCount || albumMediaList(photo).length }} 个素材
                  </span>
                  <div class="pw-photo__overlay">
                    <p class="pw-photo__desc-text">{{ photo.title || photo.description || '未命名相册' }}</p>
                  </div>
                  <div v-if="!isVisitor" class="pw-photo__actions" @click.stop>
                    <button class="pw-mini-btn" title="编辑" @click="openEdit(photo)">
                      <span class="material-symbols-outlined" style="font-size:16px">edit</span>
                    </button>
                    <button class="pw-mini-btn pw-mini-btn--danger" title="删除" @click="confirmRemove(photo)">
                      <span class="material-symbols-outlined" style="font-size:16px">delete</span>
                    </button>
                  </div>
                  <span v-if="!photo.visitorVisible" class="pw-photo__lock" title="仅我们可见">
                    <span class="material-symbols-outlined" style="font-size:14px">lock</span>
                  </span>
                </div>
                <div class="pw-photo__cap">
                  <div class="pw-photo__cap-main">
                    <span class="pw-photo__cap-loc">{{ photo.title || photo.location || '未命名相册' }}</span>
                    <span class="pw-photo__cap-meta">{{ photo.mediaCount || albumMediaList(photo).length || 0 }} 个素材</span>
                  </div>
                  <span class="pw-photo__cap-date">{{ capDate(photo.shootTime || photo.createTime) }}</span>
                </div>
              </div>
            </article>
          </section>
        </div>
      </section>
    </div>

    <!-- 触底 sentinel -->
    <div v-if="photos.length" ref="sentinelRef" class="pw-sentinel">
      <span v-if="loadingList">加载中…</span>
      <span v-else-if="!hasMore">到底啦 ♡</span>
    </div>

    <!-- 悬浮上传按钮 -->
    <button v-if="!isVisitor" class="pw-fab" @click="openCreate" title="创建相册">
      <span class="pw-fab__pulse"></span>
      <span class="material-symbols-outlined" style="font-size:24px">photo_album</span>
    </button>

    <!-- 上传/编辑弹窗 -->
    <el-dialog
      v-model="editorVisible"
      width="min(94vw, 720px)"
      class="pv4-dialog pw-editor-dialog"
      modal-class="pw-blur-mask"
      destroy-on-close
      append-to-body
    >
      <div class="pv4-dialog-content">
        <div class="sv-dialog-top">
          <h3 class="pv4-dialog-title">{{ editorMode === 'create' ? '创建相册' : '编辑相册' }}</h3>
          <button class="sv-dialog-close" @click="editorVisible = false">×</button>
        </div>

        <input ref="fileInputRef" type="file" multiple hidden @change="handlePick">

        <div class="pw-media-order-head">
          <strong>素材顺序</strong>
          <span>{{ form.mediaList.length }} 个</span>
          <div v-if="form.mediaList.length" class="pw-media-order-actions">
            <button type="button" title="向前滑动" @click="scrollMediaTrack(-1)">
              <span class="material-symbols-outlined">chevron_left</span>
            </button>
            <button type="button" title="向后滑动" @click="scrollMediaTrack(1)">
              <span class="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        </div>

        <div
          ref="mediaTrackRef"
          class="pw-media-track"
          :class="{ 'is-sorting': draggingMediaIndex !== null }"
          @pointermove="onSortPointerMove"
          @pointerup="finishMediaSort"
          @pointercancel="finishMediaSort"
        >
          <TransitionGroup name="pw-media" tag="div" class="pw-media-track__list">
            <div
              v-for="(media, idx) in form.mediaList"
              :key="media._sortKey"
              class="pw-media-card"
              :class="{ 'is-cover': isCoverMedia(media), 'is-dragging': draggingMediaIndex === idx }"
              :data-sort-index="idx"
            >
              <div class="pw-media-card__thumb">
                <img v-if="media.type === 'image' || media.coverUrl" :src="media.coverUrl || media.url" alt="">
                <div v-else class="pw-media-card__fallback">
                  <span class="material-symbols-outlined" style="font-size:28px">{{ mediaIcon(media.type) }}</span>
                  <span>{{ mediaTypeLabel(media.type) }}</span>
                </div>
                <button
                  type="button"
                  class="pw-media-card__sort-handle"
                  title="拖动调整顺序"
                  :aria-label="`拖动第 ${idx + 1} 个素材调整顺序`"
                  @pointerdown.stop.prevent="startMediaSort($event, idx)"
                >
                  <span class="material-symbols-outlined">drag_indicator</span>
                </button>
                <span class="pw-media-card__position">{{ idx + 1 }}</span>
                <span v-if="isCoverMedia(media)" class="pw-media-card__cover-tag">封面</span>
                <div class="pw-media-card__overlay">
                  <button type="button" class="pw-media-card__overlay-btn" :class="{ active: isCoverMedia(media) }" @click.stop="toggleCoverMedia(media, !isCoverMedia(media))">
                    {{ isCoverMedia(media) ? '已是封面' : '设为封面' }}
                  </button>
                  <button type="button" class="pw-media-card__overlay-btn pw-media-card__overlay-btn--del" @click.stop="removeMedia(idx)">删除</button>
                </div>
              </div>
              <div class="pw-media-card__alias">
                <el-input
                  v-model="media.alias"
                  class="pw-media-card__alias-input"
                  type="textarea"
                  :autosize="{ minRows: 2 }"
                  resize="none"
                  placeholder="备注别名"
                />
              </div>
            </div>

            <div key="add-media" class="pw-media-card pw-media-card--add" @click="pickFile">
              <div class="pw-media-card__add-inner" v-loading="uploadingFile">
                <span class="pw-media-card__add-icon">+</span>
                <span class="pw-media-card__add-text">添加素材</span>
              </div>
            </div>
          </TransitionGroup>
        </div>

        <div class="pv4-dialog-form">
          <div class="pv4-form-grid">
            <div class="pv4-field pv4-field--full">
              <label>相册标题</label>
              <el-input v-model="form.title" placeholder="例如：五一旅行相册" />
            </div>
            <div class="pv4-field pv4-field--full">
              <label>相册描述</label>
              <el-input v-model="form.description" type="textarea" :rows="3" placeholder="写点关于这本相册的故事…" resize="none" />
            </div>
            <div class="pv4-field">
              <label>地点</label>
              <el-input v-model="form.location" placeholder="例如：青岛栈桥" />
            </div>
            <div class="pv4-field">
              <label>心情</label>
              <el-input v-model="form.mood" placeholder="开心 / 甜蜜 / 想念…" />
            </div>
            <div class="pv4-field">
              <label>天气</label>
              <el-input v-model="form.weather" placeholder="晴 / 阴 / 微风…" />
            </div>
            <div class="pv4-field">
              <label>拍摄日期</label>
              <el-date-picker
                v-model="form.shootTime"
                type="date"
                format="YYYY年M月D日"
                value-format="YYYY-MM-DD"
                placeholder="选择日期"
              />
            </div>
            <div class="pv4-field pv4-field--full pw-switch-row">
              <span>
                <strong>访客可见</strong>
                <small>关闭后仅我们之间可看到</small>
              </span>
              <el-switch v-model="form.visitorVisible" :active-value="1" :inactive-value="0" />
            </div>
          </div>
        </div>

        <div class="pv4-dialog-footer">
          <el-button round @click="editorVisible = false">取消</el-button>
          <el-button type="danger" round :loading="submitting" @click="submitPhoto">
            {{ editorMode === 'create' ? '贴上去' : '保存修改' }}
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 详情 Lightbox -->
    <el-dialog
      v-model="detailVisible"
      width="min(94vw, 880px)"
      class="pv4-dialog pw-detail-dialog"
      modal-class="pw-blur-mask"
      destroy-on-close
      append-to-body
    >
      <div v-if="detail.photo" class="pw-detail" v-loading="loadingDetail">
        <button class="pw-detail__close" @click="detailVisible = false">×</button>

        <!-- 封面大图 -->
        <div class="pw-detail__cover" @click="openFullscreenFromMedia(detail.mediaList[0] || detail.photo)">
          <img :src="getAlbumCover(detail.photo)" :alt="detail.photo.title || '相册封面'">
          <div class="pw-detail__cover-gradient"></div>
          <div class="pw-detail__cover-info">
            <h2>{{ detail.photo.title || '未命名相册' }}</h2>
            <div class="pw-detail__cover-meta">
              <span v-if="detail.photo.location">
                <span class="material-symbols-outlined" style="font-size:15px">location_on</span>
                {{ detail.photo.location }}
              </span>
              <span>{{ fullDate(detail.photo.shootTime || detail.photo.createTime) }}</span>
            </div>
          </div>
          <div class="pw-detail__cover-zoom">
            <span class="material-symbols-outlined" style="font-size:20px">zoom_in</span>
          </div>
        </div>

        <!-- 用户 + 操作 -->
        <div class="pw-detail__bar">
          <div class="pw-detail__avatar">
            <img v-if="detail.creatorAvatar" :src="detail.creatorAvatar" alt="">
            <span v-else>{{ (detail.creatorNickname || '?').charAt(0) }}</span>
          </div>
          <div class="pw-detail__bar-info">
            <strong>{{ detail.creatorNickname || '未知' }}</strong>
            <span>{{ detail.mediaCount || detail.mediaList.length }} 个素材 · {{ detail.photo.visitorVisible ? '公开' : '仅我们可见' }}</span>
          </div>
          <div v-if="!isVisitor" class="pw-detail__bar-actions">
            <button class="pw-detail__bar-btn" @click="openEdit(detail.photo)">
              <span class="material-symbols-outlined" style="font-size:16px">edit</span> 编辑
            </button>
            <button class="pw-detail__bar-btn pw-detail__bar-btn--danger" @click="confirmRemove(detail.photo)">
              <span class="material-symbols-outlined" style="font-size:16px">delete</span> 删除
            </button>
          </div>
        </div>

        <!-- 描述 + 标签 -->
        <div v-if="detail.photo.description || detail.photo.mood || detail.photo.weather" class="pw-detail__story">
          <p v-if="detail.photo.description">{{ detail.photo.description }}</p>
          <div v-if="detail.photo.mood || detail.photo.weather" class="pw-detail__tags">
            <span v-if="detail.photo.mood" class="pw-detail__tag">
              <span class="material-symbols-outlined" style="font-size:14px">mood</span> {{ detail.photo.mood }}
            </span>
            <span v-if="detail.photo.weather" class="pw-detail__tag">
              <span class="material-symbols-outlined" style="font-size:14px">cloud</span> {{ detail.photo.weather }}
            </span>
          </div>
        </div>

        <!-- 素材画廊 -->
        <div v-if="detail.mediaList.length > 0" class="pw-detail__gallery">
          <div class="pw-detail__gallery-grid">
            <div
              v-for="(media, idx) in detail.mediaList"
              :key="media.id || `${media.url}-${idx}`"
              class="pw-detail__item"
            >
              <button type="button" class="pw-detail__thumb" @click="openFullscreenFromMedia(media)">
                <img v-if="media.type === 'image' || media.coverUrl" :src="media.coverUrl || media.url" alt="">
                <div v-else class="pw-detail__thumb-fallback">
                  <span class="material-symbols-outlined" style="font-size:32px">{{ mediaIcon(media.type) }}</span>
                  <span>{{ mediaTypeLabel(media.type) }}</span>
                </div>
                <div class="pw-detail__thumb-hover">
                  <span class="material-symbols-outlined" style="font-size:28px">zoom_in</span>
                </div>
              </button>
              <p v-if="media.alias" class="pw-detail__alias">{{ media.alias }}</p>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 全屏图片预览（Teleport 到 body，保证在所有 dialog 之上） -->
    <Teleport to="body">
      <div v-if="fullscreenUrl" class="pw-fullscreen" @click="fullscreenUrl = ''">
        <img v-if="fullscreenType === 'image'" :src="fullscreenUrl" alt="" @click.stop>
        <video v-else-if="fullscreenType === 'video'" :src="fullscreenUrl" controls autoplay playsinline @click.stop></video>
        <audio v-else class="pw-fullscreen__audio" :src="fullscreenUrl" controls autoplay @click.stop></audio>
        <button class="pw-fullscreen__close" @click="fullscreenUrl = ''">×</button>
      </div>
    </Teleport>

  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import gsap from 'gsap'
import {
  createPhoto,
  deletePhoto,
  getPhotoDetail,
  getPhotoPage,
  uploadPhotoMedia,
  updatePhoto
} from '../api/photo'

const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem('us_space_user') || 'null') } catch { return null }
}
const isVisitor = computed(() => getStoredUser()?.role === 'visitor')

const wallRef = ref(null)
const sentinelRef = ref(null)
const fileInputRef = ref(null)
const mediaTrackRef = ref(null)

const photos = ref([])
const pagination = reactive({ pageNum: 1, pageSize: 20, total: 0 })
const filters = reactive({ location: '', keyword: '', dateRange: null })
const loadingList = ref(false)
const loadingDetail = ref(false)
const uploadingFile = ref(false)
const submitting = ref(false)

const editorVisible = ref(false)
const editorMode = ref('create')
const editingId = ref(null)
const draggingMediaIndex = ref(null)
let mediaSortPointerId = null
let mediaSortKeySeed = 0
let mediaSortHandle = null
let mediaSortLastMoveAt = 0

const form = reactive({
  title: '',
  coverUrl: '',
  url: '',
  description: '',
  shootTime: '',
  location: '',
  mood: '',
  weather: '',
  visitorVisible: 1,
  mediaList: []
})

const detailVisible = ref(false)
const detail = reactive({ photo: null, mediaList: [], mediaCount: 0, tags: [], creatorNickname: '', creatorAvatar: '' })
const fullscreenUrl = ref('')
const fullscreenType = ref('image')

const hasMore = computed(() => photos.value.length < pagination.total)

const hasActiveFilter = computed(() => {
  return !!(filters.location || filters.keyword || (filters.dateRange && filters.dateRange.length))
})

const groupedPhotos = computed(() => {
  const yearMap = {}
  photos.value.forEach((photo) => {
    const raw = photo.shootTime || photo.createTime || ''
    const d = new Date(String(raw).replace(' ', 'T'))
    const valid = !Number.isNaN(d.getTime())
    const year = valid ? d.getFullYear() : '未知'
    const month = valid ? d.getMonth() + 1 : 0
    const monthLabel = valid ? `${d.getFullYear()}年${month}月` : '未知时间'
    const key = `${year}-${month}`

    if (!yearMap[year]) {
      yearMap[year] = { year, title: `${year}年`, count: 0, months: {} }
    }
    yearMap[year].count++

    if (!yearMap[year].months[key]) {
      yearMap[year].months[key] = { key, label: monthLabel, photos: [] }
    }
    yearMap[year].months[key].photos.push(photo)
  })

  return Object.values(yearMap)
    .sort((a, b) => (b.year === '未知' ? -1 : b.year) - (a.year === '未知' ? -1 : a.year))
    .map((g) => ({
      ...g,
      months: Object.values(g.months).sort((a, b) => {
        const ma = parseInt(a.key.split('-')[1]) || 0
        const mb = parseInt(b.key.split('-')[1]) || 0
        return mb - ma
      })
    }))
})

const capDate = (s) => {
  if (!s) return ''
  const d = new Date(String(s).replace(' ', 'T'))
  if (Number.isNaN(d.getTime())) return s
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`
}

const fullDate = (s) => {
  if (!s) return ''
  const d = new Date(String(s).replace(' ', 'T'))
  if (Number.isNaN(d.getTime())) return s
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
}

const seeded = (id, salt = 0) => {
  const x = Math.sin((Number(id) || 0) * 9301 + 49297 + salt * 131) * 10000
  return x - Math.floor(x)
}

const photoStyle = (photo, index) => {
  const rot = (seeded(photo.id, 1) * 8 - 4).toFixed(2)
  const shift = (seeded(photo.id, 2) * 6 - 3).toFixed(2)
  return {
    '--rot': `${rot}deg`,
    '--shift': `${shift}px`,
    animationDelay: `${(index % 12) * 45}ms`
  }
}

const tapeStyle = (id) => {
  const rot = (seeded(id, 3) * 10 - 5).toFixed(2)
  const left = (30 + seeded(id, 4) * 40).toFixed(1)
  const palette = ['#ffd6e0', '#ffe8c2', '#d8ecff', '#d8f3dc', '#fde2ff']
  const color = palette[Math.floor(seeded(id, 5) * palette.length)]
  return {
    left: `${left}%`,
    background: `linear-gradient(135deg, ${color}cc, ${color}66)`,
    transform: `translateX(-50%) rotate(${rot}deg)`
  }
}

const floatStyle = (n) => ({
  left: `${(n * 53) % 100}%`,
  top: `${(n * 37) % 100}%`,
  animationDelay: `${(n % 9) * 0.8}s`,
  animationDuration: `${10 + (n % 6) * 2}s`,
  opacity: 0.08 + (n % 5) * 0.02
})

const albumMediaList = (photo) => {
  if (Array.isArray(photo?.mediaList)) {
    return [...photo.mediaList].sort((a, b) => (a?.sort ?? 0) - (b?.sort ?? 0))
  }
  return []
}

const getAlbumCover = (photo) => {
  if (photo?.coverUrl) return photo.coverUrl
  const firstMedia = albumMediaList(photo)[0]
  return firstMedia?.coverUrl || firstMedia?.url || photo?.url || ''
}

const isCoverMedia = (media) => {
  if (!media) return false
  return form.coverUrl ? form.coverUrl === (media.coverUrl || media.url) : form.url === media.url
}

const setCoverMedia = (media) => {
  if (!media) return
  form.coverUrl = media.coverUrl || media.url
  form.url = media.url
}

const toggleCoverMedia = (media, checked) => {
  if (!media) return
  if (checked) {
    setCoverMedia(media)
    return
  }
  if (form.mediaList.length <= 1) {
    setCoverMedia(media)
    return
  }
  if (isCoverMedia(media)) {
    const fallback = form.mediaList.find((item) => item.url !== media.url) || form.mediaList[0]
    setCoverMedia(fallback)
  }
}

const mediaTypeLabel = (type) => {
  const map = {
    image: '图片',
    video: '视频',
    audio: '音频',
    file: '文件',
    mixed: '混合'
  }
  return map[type] || '素材'
}

const mediaIcon = (type) => {
  const map = {
    image: 'image',
    video: 'movie',
    audio: 'graphic_eq',
    file: 'description',
    mixed: 'perm_media'
  }
  return map[type] || 'perm_media'
}

const extractFileName = (url) => {
  const cleanUrl = String(url || '').split('?')[0].split('#')[0]
  return cleanUrl.split('/').pop() || '未命名文件'
}

const normalizePhotoItem = (item) => {
  if (!item || typeof item !== 'object') return null
  if (item.photo && typeof item.photo === 'object') {
    return {
      ...item.photo,
      mediaList: Array.isArray(item.mediaList) ? item.mediaList : [],
      mediaCount: item.mediaCount ?? (Array.isArray(item.mediaList) ? item.mediaList.length : 0),
      tags: Array.isArray(item.tags) ? item.tags : []
    }
  }
  return item
}

const normalizePhotoPage = (payload) => {
  const list = Array.isArray(payload?.records)
    ? payload.records
    : Array.isArray(payload?.list)
      ? payload.list
      : Array.isArray(payload)
        ? payload
        : []

  return {
    total: payload?.total ?? list.length,
    list: list.map(normalizePhotoItem).filter(Boolean)
  }
}

const getMediaType = (photo) => {
  const explicitType = photo?.mediaType || photo?.type || ''
  if (explicitType) return explicitType

  const url = String(photo?.url || '').toLowerCase()
  if (/\.(mp4|mov|webm|m4v|avi|mkv)(\?|#|$)/.test(url)) return 'video'
  if (/\.(mp3|wav|ogg|m4a|aac|flac)(\?|#|$)/.test(url)) return 'audio'
  if (/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|7z|txt)(\?|#|$)/.test(url)) return 'file'
  return 'image'
}

const normalizeMediaUpload = (payload, index = 0) => {
  const url = payload?.url || ''
  return {
    type: getMediaType(payload),
    url,
    coverUrl: payload?.contentType?.startsWith('image/') ? url : '',
    alias: '',
    fileName: payload?.fileName || extractFileName(url),
    contentType: payload?.contentType || '',
    sort: index,
    _sortKey: `upload-${Date.now()}-${mediaSortKeySeed++}`
  }
}

const withSortKey = (media, index) => ({
  ...media,
  sort: media?.sort ?? index,
  alias: media?.alias || '',
  _sortKey: media?._sortKey || `media-${media?.id || media?.url || 'item'}-${mediaSortKeySeed++}`
})

const syncMediaSort = () => {
  form.mediaList = form.mediaList.map((media, index) => ({ ...media, sort: index }))
}

const scrollMediaTrack = (direction) => {
  const track = mediaTrackRef.value
  if (!track) return
  track.scrollBy({ left: direction * Math.max(220, track.clientWidth * 0.72), behavior: 'smooth' })
}

const startMediaSort = (event, index) => {
  if (event.button !== undefined && event.button !== 0) return
  draggingMediaIndex.value = index
  mediaSortPointerId = event.pointerId
  mediaSortHandle = event.currentTarget
  mediaSortLastMoveAt = 0
  mediaSortHandle?.setPointerCapture?.(event.pointerId)
}

const onSortPointerMove = (event) => {
  if (draggingMediaIndex.value === null || event.pointerId !== mediaSortPointerId) return
  const track = mediaTrackRef.value
  if (!track) return

  const bounds = track.getBoundingClientRect()
  const edgeSize = Math.min(72, bounds.width * 0.18)
  if (event.clientX < bounds.left + edgeSize) {
    track.scrollLeft -= 12
  } else if (event.clientX > bounds.right - edgeSize) {
    track.scrollLeft += 12
  }

  const targetCard = document.elementFromPoint(event.clientX, event.clientY)?.closest?.('[data-sort-index]')
  if (!targetCard || !track.contains(targetCard)) return
  const targetIndex = Number(targetCard.dataset.sortIndex)
  const fromIndex = draggingMediaIndex.value
  if (!Number.isInteger(targetIndex) || targetIndex === fromIndex) return
  if (event.timeStamp - mediaSortLastMoveAt < 80) return

  const nextList = [...form.mediaList]
  const [moved] = nextList.splice(fromIndex, 1)
  nextList.splice(targetIndex, 0, moved)
  form.mediaList = nextList
  draggingMediaIndex.value = targetIndex
  mediaSortLastMoveAt = event.timeStamp
}

const finishMediaSort = (event) => {
  if (draggingMediaIndex.value === null) return
  if (event?.pointerId !== undefined && event.pointerId !== mediaSortPointerId) return
  if (mediaSortHandle?.hasPointerCapture?.(mediaSortPointerId)) {
    mediaSortHandle.releasePointerCapture(mediaSortPointerId)
  }
  syncMediaSort()
  draggingMediaIndex.value = null
  mediaSortPointerId = null
  mediaSortHandle = null
  mediaSortLastMoveAt = 0
}

const buildAlbumPayload = () => {
  const mediaList = form.mediaList.map((media, index) => ({
    type: media.type,
    url: media.url,
    coverUrl: media.coverUrl || (media.type === 'image' ? media.url : ''),
    alias: media.alias || undefined,
    fileName: media.fileName || extractFileName(media.url),
    contentType: media.contentType || undefined,
    sort: index
  }))
  const leadMedia = mediaList[0]
  const mediaTypes = [...new Set(mediaList.map((media) => media.type).filter(Boolean))]
  return {
    title: form.title.trim() || undefined,
    coverUrl: form.coverUrl || getAlbumCover({ coverUrl: form.coverUrl, mediaList }),
    mediaType: mediaTypes.length > 1 ? 'mixed' : (mediaTypes[0] || undefined),
    url: leadMedia?.url || '',
    description: form.description.trim() || undefined,
    shootTime: form.shootTime ? `${form.shootTime} 00:00:00` : undefined,
    location: form.location.trim() || undefined,
    mood: form.mood.trim() || undefined,
    weather: form.weather.trim() || undefined,
    visitorVisible: form.visitorVisible,
    mediaList
  }
}

let filterTimer = null
const onFilterChange = () => {
  clearTimeout(filterTimer)
  filterTimer = setTimeout(() => fetchPhotos(true), 280)
}

const clearFilters = () => {
  filters.location = ''
  filters.keyword = ''
  filters.dateRange = null
  fetchPhotos(true)
}

const fetchPhotos = async (reset = false) => {
  if (loadingList.value) return
  if (reset) {
    pagination.pageNum = 1
    photos.value = []
  } else if (!hasMore.value && photos.value.length) {
    return
  }
  loadingList.value = true
  try {
    const data = await getPhotoPage({
      pageNum: pagination.pageNum,
      pageSize: pagination.pageSize,
      location: filters.location || undefined,
      startDate: filters.dateRange?.[0] || undefined,
      endDate: filters.dateRange?.[1] || undefined,
      keyword: filters.keyword || undefined
    })
    const page = normalizePhotoPage(data)
    if (!isMounted) return
    photos.value = reset ? page.list : [...photos.value, ...page.list]
    pagination.total = page.total || 0
    await nextTick()
    if (!isMounted) return
    animateIn(reset)
  } catch (e) {
    ElMessage.error(e.message || '加载失败')
  } finally {
    loadingList.value = false
  }
}

const animateIn = (reset) => {
  if (!wallRef.value) return
  const cards = wallRef.value.querySelectorAll('.pw-photo')
  const targets = reset ? cards : Array.from(cards).slice(-pagination.pageSize)
  if (!targets.length) return
  gsap.from(targets, {
    y: 40,
    opacity: 0,
    rotation: (i) => (i % 2 ? 6 : -6),
    duration: 0.7,
    ease: 'back.out(1.4)',
    stagger: { each: 0.04, from: 'start' },
    clearProps: 'opacity'
  })
}

const onTilt = (event) => {
  const target = event.currentTarget
  const rect = target.getBoundingClientRect()
  const x = (event.clientX - rect.left) / rect.width - 0.5
  const y = (event.clientY - rect.top) / rect.height - 0.5
  target.style.setProperty('--tilt-x', `${(-y * 8).toFixed(2)}deg`)
  target.style.setProperty('--tilt-y', `${(x * 8).toFixed(2)}deg`)
  target.classList.add('is-tilting')
}

const onTiltLeave = (event) => {
  const target = event.currentTarget
  target.style.setProperty('--tilt-x', '0deg')
  target.style.setProperty('--tilt-y', '0deg')
  target.classList.remove('is-tilting')
}

let observer = null
let isMounted = true
const setupObserver = () => {
  if (observer) observer.disconnect()
  if (!sentinelRef.value) return
  observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting && hasMore.value && !loadingList.value) {
      pagination.pageNum++
      fetchPhotos(false)
    }
  }, { rootMargin: '240px' })
  observer.observe(sentinelRef.value)
}

watch(sentinelRef, () => setupObserver())

const resetForm = () => {
  form.title = ''
  form.coverUrl = ''
  form.url = ''
  form.description = ''
  form.shootTime = ''
  form.location = ''
  form.mood = ''
  form.weather = ''
  form.visitorVisible = 1
  form.mediaList = []
  editingId.value = null
}

const openCreate = () => {
  resetForm()
  editorMode.value = 'create'
  editorVisible.value = true
}

const openEdit = async (photo) => {
  resetForm()
  editorMode.value = 'edit'
  editingId.value = photo.id
  try {
    const data = await getPhotoDetail(photo.id)
    const p = data?.photo || photo
    form.title = p.title || ''
    form.coverUrl = p.coverUrl || ''
    form.url = p.url || ''
    form.description = p.description || ''
    form.shootTime = (p.shootTime || '').slice(0, 10)
    form.location = p.location || ''
    form.mood = p.mood || ''
    form.weather = p.weather || ''
    form.visitorVisible = p.visitorVisible ?? 1
    form.mediaList = albumMediaList(data || photo).map(withSortKey)
  } catch (e) {
    form.title = photo.title || ''
    form.coverUrl = photo.coverUrl || ''
    form.url = photo.url || ''
    form.description = photo.description || ''
    form.location = photo.location || ''
    form.mood = photo.mood || ''
    form.weather = photo.weather || ''
    form.visitorVisible = photo.visitorVisible ?? 1
    form.mediaList = albumMediaList(photo).map(withSortKey)
  }
  editorVisible.value = true
  detailVisible.value = false
}

const pickFile = () => {
  if (uploadingFile.value) return
  fileInputRef.value?.click()
}

const handlePick = async (e) => {
  const files = Array.from(e.target.files || [])
  if (files.length) await uploadMediaFiles(files)
  e.target.value = ''
}

const uploadMediaFiles = async (files) => {
  uploadingFile.value = true
  try {
    const uploaded = []
    for (const file of files) {
      const payload = await uploadPhotoMedia(file)
      uploaded.push(normalizeMediaUpload(payload))
    }
    const startIndex = form.mediaList.length
    form.mediaList = [
      ...form.mediaList,
      ...uploaded.map((media, index) => ({ ...media, sort: startIndex + index }))
    ]
    if (!form.url && form.mediaList[0]) {
      form.url = form.mediaList[0].url
    }
    if (!form.coverUrl) {
      form.coverUrl = getAlbumCover({ mediaList: form.mediaList })
    }
  } catch (e) {
    ElMessage.error(e.message || '上传失败')
  } finally {
    uploadingFile.value = false
  }
}

const removeMedia = (index) => {
  const removed = form.mediaList[index]
  form.mediaList.splice(index, 1)
  syncMediaSort()
  if (!form.mediaList.length) {
    form.url = ''
    form.coverUrl = ''
    return
  }
  if (!removed || isCoverMedia(removed)) {
    setCoverMedia(form.mediaList[0])
    return
  }
  if (!form.mediaList.some((media) => isCoverMedia(media))) {
    setCoverMedia(form.mediaList[0])
  }
}

const submitPhoto = async () => {
  if (!form.mediaList.length) {
    ElMessage.warning('先上传至少一个素材吧')
    return
  }
  submitting.value = true
  try {
    const payload = buildAlbumPayload()
    if (editorMode.value === 'create') {
      await createPhoto(payload)
    } else {
      await updatePhoto(editingId.value, payload)
    }
    editorVisible.value = false
    await fetchPhotos(true)
  } catch (e) {
    ElMessage.error(e.message || '保存失败')
  } finally {
    submitting.value = false
  }
}

const openFullscreen = (photo) => {
  fullscreenUrl.value = photo?.url || ''
  fullscreenType.value = getMediaType(photo)
}

const openFullscreenFromMedia = (media) => {
  openFullscreen(media)
}

const openDetail = async (photo) => {
  detail.photo = photo
  detail.mediaList = albumMediaList(photo)
  detail.mediaCount = photo.mediaCount || detail.mediaList.length || 0
  detail.tags = []
  detail.creatorNickname = ''
  detail.creatorAvatar = ''
  detailVisible.value = true
  loadingDetail.value = true
  try {
    const data = await getPhotoDetail(photo.id)
    detail.photo = data?.photo || photo
    detail.mediaList = albumMediaList(data || photo)
    detail.mediaCount = data?.mediaCount ?? detail.mediaList.length
    detail.tags = []
    detail.creatorNickname = data?.creatorNickname || ''
    detail.creatorAvatar = data?.creatorAvatar || ''
  } catch (e) {
    /* ignore */
  } finally {
    loadingDetail.value = false
  }
}

const confirmRemove = async (photo) => {
  try {
    await ElMessageBox.confirm('确定删除这张照片？', '删除照片', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await deletePhoto(photo.id)
    detailVisible.value = false
    photos.value = photos.value.filter((p) => p.id !== photo.id)
    pagination.total = Math.max(0, pagination.total - 1)
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(e.message || '删除失败')
  }
}

onMounted(async () => {
  await fetchPhotos(true)
  await nextTick()
  setupObserver()
})

onBeforeUnmount(() => {
  isMounted = false
  finishMediaSort()
  if (observer) observer.disconnect()
  clearTimeout(filterTimer)
  if (wallRef.value) gsap.killTweensOf(wallRef.value.querySelectorAll('.pw-photo'))
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@500;600;700&display=swap');

/* ═══════ Design Tokens ═══════ */
.pw-page {
  --pw-bg: #fff8f5;
  --pw-surface: #f5ece8;
  --pw-surface-high: #efe6e2;
  --pw-surface-low: #fbf2ed;
  --pw-primary: #7b5455;
  --pw-primary-container: #f4c2c2;
  --pw-on-surface: #1f1b18;
  --pw-on-surface-variant: #504444;
  --pw-outline: #827473;
  --pw-outline-variant: #d4c2c2;
  --pw-inverse-surface: #34302d;
  --pw-radius: 12px;
  position: relative;
  font-family: 'Plus Jakarta Sans', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 40px 120px;
  min-height: 100vh;
  overflow: hidden;
}

/* ═══════ Background hearts ═══════ */
.pw-bg { position: absolute; inset: 0; pointer-events: none; z-index: 0; }
.pw-bg__heart {
  position: absolute;
  font-size: 34px;
  color: var(--pw-primary-container);
  animation: pwFloat linear infinite;
  filter: blur(0.3px);
}
@keyframes pwFloat {
  0%   { transform: translate(0, 0) rotate(0deg); }
  50%  { transform: translate(20px, -30px) rotate(10deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
}

/* ═══════ Hero ═══════ */
.pw-hero {
  position: relative; z-index: 1;
  text-align: center;
  padding: 32px 0 24px;
  max-width: 640px;
  margin: 0 auto;
}
.pw-hero h1 {
  font-family: 'Noto Serif', serif;
  font-size: 48px; font-weight: 400; line-height: 1.2;
  color: var(--pw-primary);
  margin: 0 0 8px;
}
.pw-hero p {
  font-size: 18px; line-height: 1.6;
  color: var(--pw-on-surface-variant);
  margin: 0;
}

/* ═══════ Filter bar ═══════ */
.pw-filter {
  position: relative; z-index: 1;
  background: rgba(255,255,255,0.8);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(212,194,194,0.3);
  border-radius: var(--pw-radius);
  padding: 14px 20px;
  margin-bottom: 32px;
  box-shadow: 0 20px 40px rgba(123,84,85,0.05);
}
.pw-filter__row { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; }
.pw-filter__input {
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--pw-surface-low);
  border: 1px solid var(--pw-surface-high);
  border-radius: 8px;
  padding: 7px 12px;
  color: var(--pw-outline);
  min-width: 200px;
  transition: border-color 0.2s;
}
.pw-filter__input:focus-within { border-color: var(--pw-primary-container); }
.pw-filter__input .material-symbols-outlined { font-size: 18px; color: var(--pw-outline); }
.pw-filter__input input {
  border: 0; outline: 0; background: transparent;
  font-size: 14px; color: var(--pw-on-surface); width: 100%;
  font-family: inherit;
}
.pw-filter__input--keyword { flex: 1; min-width: 160px; }
.pw-filter__date { min-width: 260px; }
.pw-filter__clear {
  display: inline-flex; align-items: center; gap: 4px;
  border: 1px solid var(--pw-surface-high);
  background: var(--pw-surface-low);
  border-radius: 999px;
  padding: 6px 16px;
  font-size: 13px; font-weight: 600;
  color: var(--pw-primary);
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.pw-filter__clear:hover {
  background: var(--pw-primary-container);
  border-color: var(--pw-primary-container);
}

@media (max-width: 820px) {
  .pw-filter__row { flex-direction: column; }
  .pw-filter__input, .pw-filter__date { width: 100%; }
}

/* ═══════ Year / Month groups ═══════ */
.pw-groups { position: relative; z-index: 1; }
.pw-year + .pw-year { margin-top: 56px; }
.pw-year__bar {
  display: flex; align-items: center; gap: 16px;
  margin-bottom: 20px;
  position: sticky; top: 0; z-index: 6;
  padding: 10px 0;
  background: linear-gradient(180deg, rgba(255,248,245,0.95) 60%, rgba(255,248,245,0));
  backdrop-filter: blur(6px);
}
.pw-year__title {
  margin: 0;
  font-family: 'Noto Serif', serif;
  font-size: 32px; font-weight: 400;
  color: var(--pw-primary);
  white-space: nowrap;
}
.pw-year__line {
  flex: 1; height: 1px;
  background: linear-gradient(90deg, var(--pw-outline-variant), transparent);
}
.pw-year__count {
  font-size: 13px; font-weight: 600; letter-spacing: 0.05em;
  color: var(--pw-on-surface-variant);
  background: var(--pw-surface);
  padding: 3px 12px; border-radius: 999px;
  white-space: nowrap;
}

.pw-month { margin-bottom: 32px; }
.pw-month:last-child { margin-bottom: 0; }
.pw-month__title {
  margin: 0 0 18px;
  font-family: 'Noto Serif', serif;
  font-size: 20px; font-weight: 500;
  color: var(--pw-on-surface);
  display: inline-flex; align-items: baseline; gap: 10px;
  padding-left: 14px;
  border-left: 3px solid var(--pw-primary-container);
}
.pw-month__title small {
  font-size: 12px; color: var(--pw-outline);
  font-weight: 400;
}

@media (max-width: 820px) {
  .pw-year + .pw-year { margin-top: 40px; }
  .pw-year__title { font-size: 26px; }
  .pw-month__title { font-size: 17px; }
}

/* ═══════ Photo wall (masonry) ═══════ */
.pw-wall {
  position: relative; z-index: 1;
  column-count: 3; column-gap: 24px;
}
@media (max-width: 900px)  { .pw-wall { column-count: 2; column-gap: 18px; } }
@media (max-width: 560px)  { .pw-wall { column-count: 1; } }

/* ═══════ Photo card (polaroid) ═══════ */
.pw-photo {
  --rot: 0deg;
  --shift: 0px;
  --tilt-x: 0deg;
  --tilt-y: 0deg;
  position: relative;
  break-inside: avoid;
  margin: 0 0 24px;
  cursor: zoom-in;
  transform: translateX(var(--shift)) rotate(var(--rot));
  transition: transform 0.5s cubic-bezier(.2,.9,.3,1), filter 0.4s;
  will-change: transform;
}
.pw-photo__inner {
  background: #fff;
  padding: 12px 12px 10px;
  border-radius: 6px;
  box-shadow:
    0 2px 4px rgba(123,84,85,0.06),
    0 12px 24px rgba(123,84,85,0.12);
  transform: perspective(900px) rotateX(var(--tilt-x)) rotateY(var(--tilt-y));
  transform-style: preserve-3d;
  transition: box-shadow 0.4s, transform 0.2s ease-out;
}
.pw-photo.is-tilting .pw-photo__inner { transition: transform 0.05s linear; }
.pw-photo__tape {
  position: absolute;
  top: -10px;
  width: 88px; height: 22px;
  border-radius: 2px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.06);
  pointer-events: none;
  z-index: 3;
  backdrop-filter: blur(2px);
}
.pw-photo__img-wrap {
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  background: var(--pw-surface);
}
.pw-photo__img-wrap img {
  display: block; width: 100%; height: auto; object-fit: cover;
  transition: transform 0.7s cubic-bezier(.2,.9,.3,1);
}
.pw-photo__media-card {
  position: relative;
}
.pw-photo__media-card--audio {
  min-height: 260px;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #f8ede7, #efe3dc);
}
.pw-photo__audio-cover {
  min-height: 260px;
  width: 100%;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 10px;
  color: var(--pw-primary);
  font-weight: 700;
}
.pw-photo__media-badge {
  position: absolute; left: 10px; bottom: 10px;
  display: inline-flex; align-items: center; gap: 4px;
  background: rgba(0,0,0,0.55); color: #fff;
  padding: 5px 10px; border-radius: 999px;
  font-size: 12px; font-weight: 600; z-index: 4;
  backdrop-filter: blur(6px);
}
.pw-photo__count-badge {
  position: absolute; right: 10px; bottom: 10px;
  display: inline-flex; align-items: center; gap: 4px;
  background: rgba(0,0,0,0.55); color: #fff;
  padding: 5px 10px; border-radius: 999px;
  font-size: 11px; font-weight: 600; z-index: 4;
  backdrop-filter: blur(6px);
}

/* Hover overlay from design */
.pw-photo__overlay {
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(52,48,45,0.7), transparent 60%);
  opacity: 0;
  transition: opacity 0.3s;
  display: flex; flex-direction: column; justify-content: flex-end;
  padding: 20px 16px;
  pointer-events: none;
}
.pw-photo__tag {
  display: inline-block;
  background: rgba(255,255,255,0.2);
  backdrop-filter: blur(8px);
  color: #fff;
  font-size: 13px; font-weight: 600; letter-spacing: 0.05em;
  padding: 4px 12px;
  border-radius: 999px;
  margin-bottom: 6px;
  width: fit-content;
}
.pw-photo__desc-text {
  margin: 0;
  color: rgba(255,255,255,0.9);
  font-size: 14px; line-height: 1.5;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
}

.pw-photo:hover {
  transform: translateX(0) rotate(0deg) translateY(-10px) scale(1.02);
  z-index: 5;
}
.pw-photo:hover .pw-photo__inner {
  box-shadow:
    0 4px 8px rgba(123,84,85,0.08),
    0 28px 56px rgba(123,84,85,0.22);
}
.pw-photo:hover .pw-photo__img-wrap img { transform: scale(1.05); }
.pw-photo:hover .pw-photo__overlay { opacity: 1; }

.pw-photo__cap {
  display: flex; justify-content: space-between; align-items: baseline; gap: 8px;
  padding: 10px 4px 4px;
  font-family: 'Caveat', 'Kaiti SC', 'STKaiti', cursive;
  color: var(--pw-on-surface-variant);
}
.pw-photo__cap-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.pw-photo__cap-loc { font-size: 17px; font-weight: 600; color: var(--pw-on-surface); }
.pw-photo__cap-meta { font-size: 12px; color: var(--pw-outline); font-family: inherit; }
.pw-photo__cap-date { font-size: 14px; color: var(--pw-outline); }
.pw-photo__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 4px 6px;
}
.pw-photo__chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--pw-surface-low);
  border: 1px solid var(--pw-surface-high);
  color: var(--pw-on-surface-variant);
  font-size: 11px;
  line-height: 1.2;
}

.pw-photo__actions {
  position: absolute; top: 10px; right: 10px;
  display: flex; gap: 6px;
  opacity: 0; transform: translateY(-4px);
  transition: all 0.28s ease;
  z-index: 4;
}
.pw-photo:hover .pw-photo__actions { opacity: 1; transform: translateY(0); }
.pw-mini-btn {
  width: 30px; height: 30px; border-radius: 50%;
  border: 0; cursor: pointer;
  background: rgba(255,255,255,0.92);
  color: var(--pw-on-surface-variant);
  display: inline-flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  transition: all 0.2s;
}
.pw-mini-btn:hover { background: #fff; color: var(--pw-primary); transform: scale(1.08); }
.pw-mini-btn--danger:hover { color: #ba1a1a; }
.pw-photo__lock {
  position: absolute; left: 10px; top: 10px;
  background: rgba(0,0,0,0.48); color: #fff;
  display: inline-flex; align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  z-index: 4;
  backdrop-filter: blur(4px);
}

/* ═══════ Sentinel / Empty ═══════ */
.pw-sentinel {
  position: relative; z-index: 1;
  text-align: center; padding: 28px 0 10px;
  color: var(--pw-outline); font-size: 13px; letter-spacing: 2px;
}
.pw-empty {
  position: relative; z-index: 1;
  min-height: 360px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 18px; color: var(--pw-on-surface-variant);
}
.pw-btn-primary {
  display: inline-flex; align-items: center; gap: 4px;
  border: none;
  background: var(--pw-primary-container);
  color: var(--pw-primary);
  font-size: 14px; font-weight: 600;
  padding: 10px 24px; border-radius: 999px;
  cursor: pointer; transition: all 0.2s;
}
.pw-btn-primary:hover {
  background: var(--pw-primary);
  color: #fff;
}

/* ═══════ FAB ═══════ */
.pw-fab {
  position: fixed;
  right: 28px; bottom: 32px;
  width: 60px; height: 60px; border-radius: 50%;
  border: 0; cursor: pointer;
  background: var(--pw-primary);
  color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  box-shadow: 0 14px 30px rgba(123,84,85,0.35);
  z-index: 20;
  transition: transform 0.3s, background 0.2s;
}
.pw-fab:hover { transform: scale(1.08) rotate(10deg); background: var(--pw-on-surface-variant); }
.pw-fab__pulse {
  position: absolute; inset: 0; border-radius: 50%;
  background: inherit; opacity: 0.4;
  animation: pwPulse 2s ease-out infinite;
  z-index: -1;
}
@keyframes pwPulse {
  0%   { transform: scale(1);   opacity: 0.4; }
  100% { transform: scale(1.5); opacity: 0; }
}

/* ═══════ Upload/Edit drop zone ═══════ */
.pw-drop {
  position: relative;
  margin: 18px 0 8px;
  border: 2px dashed var(--pw-outline-variant);
  border-radius: var(--pw-radius);
  min-height: 200px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  background: var(--pw-surface-low);
  overflow: hidden;
  transition: all 0.25s;
}
.pw-drop.is-dragging { border-color: var(--pw-primary); background: var(--pw-primary-container); }
.pw-drop.has-image { border-style: solid; border-color: var(--pw-surface-high); min-height: auto; }
.pw-drop img { width: 100%; max-height: 320px; object-fit: cover; display: block; }
.pw-drop__tip { text-align: center; color: var(--pw-outline); }
.pw-drop__tip p { margin: 0; font-size: 14px; color: var(--pw-on-surface); font-weight: 600; }
.pw-drop__tip span { font-size: 12px; color: var(--pw-outline); }
.pw-drop__video-preview {
  position: relative;
  width: 100%;
}
.pw-drop__media-fallback {
  width: 100%; min-height: 240px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, #f7ece6, #ede0d8);
  color: var(--pw-primary);
}
.pw-drop__media-fallback--audio {
  min-height: 220px;
}
.pw-drop__media-fallback p {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
}
.pw-drop__media-chip {
  position: absolute; left: 12px; bottom: 12px;
  display: inline-flex; align-items: center; gap: 4px;
  background: rgba(0,0,0,0.55); color: #fff;
  padding: 6px 12px; border-radius: 999px;
  font-size: 12px; font-weight: 600;
}
.pw-drop__mask {
  position: absolute; inset: 0;
  background: rgba(255,255,255,0.7);
  display: flex; align-items: center; justify-content: center;
  color: var(--pw-primary); font-weight: 600;
}
.pw-drop--album {
  min-height: 156px;
}

.pw-media-order-head {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  margin-top: 10px;
  color: var(--pw-on-surface);
}
.pw-media-order-head strong { font-size: 14px; }
.pw-media-order-head > span { font-size: 12px; color: var(--pw-outline); }
.pw-media-order-actions {
  display: flex;
  gap: 6px;
  margin-left: auto;
}
.pw-media-order-actions button {
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--pw-surface-high);
  border-radius: 50%;
  background: #fff;
  color: var(--pw-on-surface-variant);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}
.pw-media-order-actions button:hover {
  color: var(--pw-primary);
  border-color: var(--pw-primary-container);
  background: var(--pw-surface-low);
}
.pw-media-order-actions .material-symbols-outlined { font-size: 20px; }
.pw-media-track {
  margin: 4px -4px 16px;
  padding: 8px 4px 12px;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-inline: contain;
  scroll-snap-type: inline proximity;
  scrollbar-width: thin;
  scrollbar-color: var(--pw-outline-variant) transparent;
  touch-action: pan-x;
  scroll-behavior: smooth;
}
.pw-media-track__list {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: max-content;
  min-width: 100%;
}
.pw-media-track::-webkit-scrollbar { height: 6px; }
.pw-media-track::-webkit-scrollbar-thumb {
  background: var(--pw-outline-variant);
  border-radius: 999px;
}
.pw-media-track.is-sorting {
  scroll-snap-type: none;
  cursor: grabbing;
  user-select: none;
}
.pw-media-move {
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1) !important;
}
.pw-media-card {
  position: relative;
  flex: 0 0 180px;
  width: 180px;
  scroll-snap-align: start;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(0,0,0,0.06);
  box-shadow: 0 4px 14px -4px rgba(80,60,70,0.07);
  transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.22s ease, opacity 0.18s ease;
  will-change: transform;
}
.pw-media-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 22px -6px rgba(80,60,70,0.12);
}
.pw-media-card.is-dragging {
  z-index: 5;
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 12px 28px rgba(80,60,70,0.22);
  opacity: 0.88;
}
.pw-media-card.is-cover {
  border-color: var(--pw-primary);
  box-shadow: 0 4px 16px rgba(123,84,85,0.15);
}
.pw-media-card__thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  background: var(--pw-surface-low);
}
.pw-media-card__thumb img {
  width: 100%; height: 100%;
  object-fit: cover; display: block;
  pointer-events: none;
}
.pw-media-card__fallback {
  width: 100%; height: 100%;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 8px;
  color: var(--pw-primary);
  background: linear-gradient(135deg, #f8ede7, #efe3dc);
}
.pw-media-card__cover-tag {
  position: absolute;
  top: 8px; left: 8px;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--pw-primary);
  color: #fff;
  font-size: 11px; font-weight: 700;
  letter-spacing: 0.5px;
}
.pw-media-card__sort-handle {
  position: absolute;
  z-index: 4;
  top: 8px;
  right: 8px;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid rgba(255,255,255,0.55);
  border-radius: 50%;
  background: rgba(35,28,29,0.6);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  touch-action: none;
  backdrop-filter: blur(6px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.18);
}
.pw-media-card__sort-handle:active { cursor: grabbing; }
.pw-media-card__sort-handle .material-symbols-outlined { font-size: 21px; }
.pw-media-card__position {
  position: absolute;
  z-index: 3;
  right: 8px;
  bottom: 8px;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  border-radius: 999px;
  box-sizing: border-box;
  background: rgba(35,28,29,0.62);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
}
.pw-media-card__overlay {
  position: absolute; inset: 0;
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(2px);
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.25s ease;
}
.pw-media-card:hover .pw-media-card__overlay,
.pw-media-card:focus-within .pw-media-card__overlay { opacity: 1; }
.pw-media-card__overlay-btn {
  padding: 6px 16px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.6);
  background: rgba(255,255,255,0.18);
  color: #fff;
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(4px);
}
.pw-media-card__overlay-btn:hover {
  background: rgba(255,255,255,0.32);
}
.pw-media-card__overlay-btn.active {
  background: var(--pw-primary);
  border-color: var(--pw-primary);
}
.pw-media-card__overlay-btn--del {
  border-color: rgba(255,120,120,0.6);
  color: #ffcccc;
}
.pw-media-card__overlay-btn--del:hover {
  background: rgba(186,26,26,0.55);
  border-color: rgba(186,26,26,0.7);
  color: #fff;
}
.pw-media-card__alias {
  padding: 0;
  border-top: 1px solid rgba(0,0,0,0.05);
  background: #fff;
}
.pw-media-card__alias-input {
  display: block;
  width: 100%;
}
:deep(.pw-media-card__alias-input .el-textarea__inner) {
  min-height: 54px !important;
  padding: 9px 10px;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  color: #444;
  font-size: 12px;
  line-height: 1.55;
  overflow: hidden;
  overflow-wrap: anywhere;
  transition: background 0.2s ease;
}
:deep(.pw-media-card__alias-input .el-textarea__inner::placeholder) { color: #bbb; }
:deep(.pw-media-card__alias-input .el-textarea__inner:focus) {
  background: var(--pw-surface-low);
  box-shadow: inset 0 2px 0 rgba(123,84,85,0.24);
}
.pw-media-card--add {
  min-height: 224px;
  cursor: pointer;
  border: 2px dashed rgba(0,0,0,0.08);
  background: rgba(255,255,255,0.4);
  box-shadow: none;
}
.pw-media-card--add:hover {
  border-color: rgba(123,84,85,0.3);
  background: rgba(255,255,255,0.7);
  transform: none;
}
.pw-media-card__add-inner {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  aspect-ratio: 1;
  gap: 4px;
}
.pw-media-card__add-icon {
  font-size: 32px; font-weight: 300;
  color: #bbb; line-height: 1;
  transition: color 0.25s ease;
}
.pw-media-card--add:hover .pw-media-card__add-icon { color: var(--pw-primary); }
.pw-media-card__add-text {
  font-size: 12px; color: #aaa;
  transition: color 0.25s ease;
}
.pw-media-card--add:hover .pw-media-card__add-text { color: var(--pw-primary); }

/* Visibility switch row */
.pw-switch-row {
  display: flex !important; flex-direction: row !important;
  justify-content: space-between; align-items: center;
  background: var(--pw-surface-low);
  padding: 10px 14px; border-radius: 8px;
}
.pw-switch-row strong { display: block; font-size: 14px; color: var(--pw-on-surface); }
.pw-switch-row small { color: var(--pw-outline); font-size: 12px; }

/* ═══════ Lightbox detail ═══════ */
.pw-detail {
  position: relative;
  display: flex; flex-direction: column;
}
.pw-detail__close {
  position: absolute; top: 14px; right: 14px; z-index: 10;
  width: 36px; height: 36px; border-radius: 50%;
  background: rgba(0,0,0,0.4); border: 0;
  color: #fff; font-size: 20px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.2s; line-height: 1;
  backdrop-filter: blur(6px);
}
.pw-detail__close:hover { background: rgba(0,0,0,0.6); }

/* Cover hero */
.pw-detail__cover {
  position: relative; width: 100%;
  height: 340px; overflow: hidden;
  cursor: zoom-in; background: #1a1a1a;
}
.pw-detail__cover img {
  width: 100%; height: 100%;
  object-fit: cover; display: block;
}
.pw-detail__cover-gradient {
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%, rgba(0,0,0,0.2) 100%);
  pointer-events: none;
}
.pw-detail__cover-info {
  position: absolute; bottom: 20px; left: 24px; right: 80px;
  color: #fff; z-index: 2;
}
.pw-detail__cover-info h2 {
  margin: 0 0 6px; font-size: 24px; font-weight: 700;
  line-height: 1.3; text-shadow: 0 2px 8px rgba(0,0,0,0.3);
}
.pw-detail__cover-meta {
  display: flex; align-items: center; gap: 14px;
  font-size: 13px; opacity: 0.85;
}
.pw-detail__cover-meta span {
  display: inline-flex; align-items: center; gap: 4px;
}
.pw-detail__cover-zoom {
  position: absolute; bottom: 20px; right: 24px; z-index: 2;
  width: 40px; height: 40px; border-radius: 50%;
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  color: #fff; opacity: 0;
  transition: opacity 0.25s;
}
.pw-detail__cover:hover .pw-detail__cover-zoom { opacity: 1; }

/* User bar */
.pw-detail__bar {
  display: flex; align-items: center; gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid var(--pw-surface-high);
}
.pw-detail__avatar {
  width: 38px; height: 38px; border-radius: 50%; overflow: hidden; flex-shrink: 0;
  border: 2px solid var(--pw-primary-container);
}
.pw-detail__avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.pw-detail__avatar span {
  display: flex; align-items: center; justify-content: center;
  width: 100%; height: 100%;
  background: var(--pw-primary); color: #fff; font-weight: 700; font-size: 14px;
}
.pw-detail__bar-info { flex: 1; min-width: 0; }
.pw-detail__bar-info strong { display: block; font-size: 14px; color: var(--pw-on-surface); }
.pw-detail__bar-info > span { font-size: 12px; color: var(--pw-outline); }
.pw-detail__bar-actions { display: flex; gap: 8px; flex-shrink: 0; }
.pw-detail__bar-btn {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 7px 16px; border-radius: 999px;
  border: 1px solid var(--pw-surface-high);
  background: transparent; font-size: 13px; font-weight: 600;
  color: var(--pw-on-surface-variant);
  cursor: pointer; transition: all 0.2s;
}
.pw-detail__bar-btn:hover {
  background: var(--pw-primary-container);
  border-color: var(--pw-primary-container);
  color: var(--pw-primary);
}
.pw-detail__bar-btn--danger { color: #ba1a1a; }
.pw-detail__bar-btn--danger:hover {
  background: rgba(186,26,26,0.06);
  border-color: rgba(186,26,26,0.2);
  color: #ba1a1a;
}

/* Story / description */
.pw-detail__story {
  padding: 16px 24px;
  border-bottom: 1px solid var(--pw-surface-high);
}
.pw-detail__story p {
  margin: 0 0 10px; font-size: 14px; line-height: 1.75;
  color: var(--pw-on-surface);
}
.pw-detail__story p:last-child { margin-bottom: 0; }
.pw-detail__tags { display: flex; flex-wrap: wrap; gap: 8px; }
.pw-detail__tag {
  display: inline-flex; align-items: center; gap: 4px;
  background: var(--pw-surface-low);
  border: 1px solid var(--pw-surface-high);
  border-radius: 999px; padding: 4px 14px;
  font-size: 12px; font-weight: 500; color: var(--pw-outline);
}
.pw-detail__tag .material-symbols-outlined { color: var(--pw-primary); }

/* Gallery */
.pw-detail__gallery { padding: 20px 24px 28px; }
.pw-detail__gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}
.pw-detail__item { min-width: 0; }
.pw-detail__thumb {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  position: relative; border-radius: 12px; overflow: hidden;
  aspect-ratio: 4/3; cursor: pointer;
  background: var(--pw-surface-low);
  box-shadow: 0 2px 8px rgba(80,60,70,0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.pw-detail__thumb:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(80,60,70,0.14);
}
.pw-detail__thumb img {
  width: 100%; height: 100%; object-fit: cover; display: block;
}
.pw-detail__thumb-fallback {
  width: 100%; height: 100%;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 6px;
  background: linear-gradient(135deg, #f8ede7, #efe3dc);
  color: var(--pw-primary); font-size: 12px;
}
.pw-detail__thumb-hover {
  position: absolute; inset: 0;
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(2px);
  display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 6px;
  color: #fff; opacity: 0;
  transition: opacity 0.25s ease;
}
.pw-detail__thumb:hover .pw-detail__thumb-hover { opacity: 1; }
.pw-detail__alias {
  margin: 8px 2px 4px;
  color: var(--pw-on-surface);
  font-size: 13px;
  line-height: 1.65;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* ═══════ Blur mask (global for Teleport) ═══════ */
:global(.pw-blur-mask) {
  backdrop-filter: blur(16px) saturate(1.2) !important;
  -webkit-backdrop-filter: blur(16px) saturate(1.2) !important;
  background: rgba(31,27,24,0.45) !important;
}

/* ═══════ Fullscreen image (Teleported to body, use :global) ═══════ */
:global(.pw-fullscreen) {
  position: fixed; inset: 0; z-index: 99999;
  background: rgba(0,0,0,0.92);
  backdrop-filter: blur(20px);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  cursor: zoom-out;
}
:global(.pw-fullscreen img) {
  max-width: min(94vw, 1200px);
  max-height: 92vh;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
  cursor: default;
  user-select: none;
  animation: pw-fs-zoom 0.35s cubic-bezier(0.22, 1, 0.36, 1) both;
}
@keyframes pw-fs-zoom {
  from { transform: scale(0.6); opacity: 0; }
  to   { transform: scale(1);   opacity: 1; }
}
:global(.pw-fullscreen__close) {
  position: absolute; top: 20px; right: 28px;
  width: 42px; height: 42px; border-radius: 50%;
  background: rgba(255,255,255,0.12);
  border: 0; color: #fff; font-size: 22px;
  cursor: pointer; transition: background 0.2s;
  display: flex; align-items: center; justify-content: center;
}
:global(.pw-fullscreen__close:hover) { background: rgba(255,255,255,0.2); }
:global(.pw-fullscreen video) {
  max-width: 92vw; max-height: 85vh;
  border-radius: 8px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.5);
}
:global(.pw-fullscreen__audio) {
  width: min(680px, 88vw);
}

/* ═══════ Responsive ═══════ */
@media (max-width: 820px) {
  .pw-page { padding: 0 16px 100px; }
  .pw-hero h1 { font-size: 34px; }
  .pw-hero p { font-size: 15px; }
  .pw-detail__cover { height: 240px; }
  .pw-detail__cover-info h2 { font-size: 20px; }
  .pw-detail__bar { padding: 14px 18px; }
  .pw-detail__story { padding: 14px 18px; }
  .pw-detail__gallery { padding: 16px 18px 22px; }
  .pw-detail__gallery-grid { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); }
  .pw-detail__bar-actions { gap: 6px; }
  .pw-detail__bar-btn { padding: 6px 12px; font-size: 12px; }
  .pw-media-card { flex-basis: min(46vw, 168px); width: min(46vw, 168px); }
  .pw-media-card--add { min-height: 210px; }
  .pw-fab { right: 16px; bottom: 74px; }
}
@media (max-width: 480px) {
  .pw-hero h1 { font-size: 28px; }
  .pw-hero { padding: 20px 0 16px; }
  .pw-detail__gallery-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

</style>
